import {shiftToFirstBy} from '@shared/helpers/array'

import {Account, ResultCode, ResultCodeType} from '@web/actions/priorityPaymentMethod'
import {
    BillingPayMethodsKey,
    BillingPayMethodsKeyType,
    Card,
    CardCompanyId,
    HYUNDAI_CARD_PRODUCT_CODE,
    HYUNDAI_CARD_PRODUCT_CODE_VER2,
    mapCardTypeToCardType,
    PaymentGatewayCode,
    PaymentGatewayCodeType,
    PaymentTabMethod,
    PaymentTabMethodType,
    SubscribePayMethod,
    SubscribePayMethodType,
} from '@web/core/constants'
import {BankEasyPayView, CardEasyPay, PriorityPaymentMethodState} from '@web/slices/priorityPaymentMethod/slice'

import {Card as PaymentMethodManagementCard} from '../../apis/interfaces'

export const isAccountSuccessCode = (code: ResultCodeType) => {
    const successCodes: Array<ResultCodeType> = [
        ResultCode.SUCCEED,
        ResultCode.NO_ACCOUNTS,
        ResultCode.INSPECTION,
        ResultCode.COMMON_NETWORK_INSPECTION,
    ]
    return successCodes.includes(code)
}

export const convertPaymentTabMethodToBillingPayMethod = (
    tab: PaymentTabMethodType,
): Maybe<BillingPayMethodsKeyType> => {
    switch (tab) {
        case PaymentTabMethod.RECHARGE:
            return BillingPayMethodsKey.RECHARGE_POINT
        case PaymentTabMethod.EASY_ACCOUNT:
            return BillingPayMethodsKey.BANK_EASY_PAY
        case PaymentTabMethod.EASY_CARD:
            return BillingPayMethodsKey.CARD_EASY_PAY
        case PaymentTabMethod.MOBILE:
            return BillingPayMethodsKey.MOBILE
        case PaymentTabMethod.SAMSUNG_PAY:
        case PaymentTabMethod.FAMILY_PAY:
        case PaymentTabMethod.NONE:
        case PaymentTabMethod.NORMAL:
            return null
    }
}

export const convertPaymentTabMethodToPayMethod = (tab: PaymentTabMethodType): SubscribePayMethodType => {
    switch (tab) {
        case PaymentTabMethod.RECHARGE:
            return SubscribePayMethod.RECHARGE_POINT
        case PaymentTabMethod.EASY_ACCOUNT:
            return SubscribePayMethod.BANK_EASY_PAY
        case PaymentTabMethod.EASY_CARD:
            return SubscribePayMethod.CARD_EASY_PAY
        case PaymentTabMethod.MOBILE:
            return SubscribePayMethod.MOBILE
        case PaymentTabMethod.SAMSUNG_PAY:
        case PaymentTabMethod.FAMILY_PAY:
        case PaymentTabMethod.NONE:
        case PaymentTabMethod.NORMAL:
            return SubscribePayMethod.NONE
    }
}

export const getSecondPaymentMethodByManagementPolicy = (firstPayMethodTab: PaymentTabMethodType) => {
    const policyMap: Record<PaymentTabMethodType, PaymentTabMethodType[]> = {
        [PaymentTabMethod.RECHARGE]: [PaymentTabMethod.EASY_CARD],
        [PaymentTabMethod.EASY_CARD]: [PaymentTabMethod.NONE, PaymentTabMethod.RECHARGE, PaymentTabMethod.EASY_CARD],
        [PaymentTabMethod.EASY_ACCOUNT]: [],
        [PaymentTabMethod.NORMAL]: [],
        [PaymentTabMethod.SAMSUNG_PAY]: [],
        [PaymentTabMethod.FAMILY_PAY]: [],
        [PaymentTabMethod.NONE]: [],
        [PaymentTabMethod.MOBILE]: [],
    }

    const availablePayMethodList = policyMap[firstPayMethodTab] || []

    return availablePayMethodList
}

export const convertToBillingPayMethodFrom = (pgCode?: PaymentGatewayCodeType) => {
    if (typeof pgCode === 'undefined') {
        return null
    }

    const codeMap: Record<string, keyof PriorityPaymentMethodState['payMethod']['billingPayMethods']> = {
        [PaymentGatewayCode.EASY_ACCOUNT]: BillingPayMethodsKey.BANK_EASY_PAY,
        [PaymentGatewayCode.EASY_CARD]: BillingPayMethodsKey.CARD_EASY_PAY,
        [PaymentGatewayCode.MOBILE]: BillingPayMethodsKey.MOBILE,
        [PaymentGatewayCode.PAY_POINT]: BillingPayMethodsKey.RECHARGE_POINT,
    }
    return codeMap[pgCode]
}

export const convertToPayMethodFrom = (pgCode?: PaymentGatewayCodeType) => {
    if (typeof pgCode === 'undefined') {
        return SubscribePayMethod.NONE
    }

    const codeMap: Record<PaymentGatewayCodeType, SubscribePayMethodType> = {
        [PaymentGatewayCode.EASY_CARD]: SubscribePayMethod.CARD_EASY_PAY,
        [PaymentGatewayCode.EASY_ACCOUNT]: SubscribePayMethod.BANK_EASY_PAY,
        [PaymentGatewayCode.MOBILE]: SubscribePayMethod.MOBILE,
        [PaymentGatewayCode.PAY_POINT]: SubscribePayMethod.RECHARGE_POINT,
    }
    return codeMap[pgCode]
}

export const convertBankEasyPay =
    () =>
    ({
        acctKey,
        isChkCard: associatedCheckCard,
        bnkCd: bankCode,
        bnkNm: bankName,
        maskAcctNo: maskedAccountNumber,
        nickNm: nickName,
        payBnkbCd: payPartnershipItemCode,
        payBnkbNm: payPartnershipItemName,
        regCmplTime: registrationCompletedDateTime,
        inspection,
    }: Account): BankEasyPayView => {
        return {
            accountKey: `${acctKey}`,
            associatedCheckCard,
            bankCode,
            bankName,
            maskedAccountNumber,
            nickName,
            payPartnershipItemCode,
            payPartnershipItemName,
            payable: true,
            registrationCompletedDateTime,
            maintenanceSchedule: inspection && {
                startDateTime: `${inspection.startDate} ${inspection.startTime}`,
                endDateTime: `${inspection.endDate} ${inspection.endTime}`,
            },
            invalidReason: null,
        }
    }

export const shiftToFirstByChargedAccounts = (result: BankEasyPayView[], chargeKey: string): BankEasyPayView[] => {
    const NAVER_BANK_CODE = 'MIRAEASSET_CMA'

    /** 네이버통장이 있을 경우 네이버통장이 최상단에, 아닐 경우 최근 결제한 계좌가 최상단에 위치하도록 정렬 */
    const {key, value}: {key: keyof BankEasyPayView; value: string} = result.some(
        ({payPartnershipItemCode}) => payPartnershipItemCode === NAVER_BANK_CODE,
    )
        ? {key: 'payPartnershipItemCode', value: NAVER_BANK_CODE}
        : {key: 'accountKey', value: chargeKey}

    return shiftToFirstBy<BankEasyPayView>(key, value)(result)
}

export const convertCardEasyPay =
    () =>
    ({
        cardCategory,
        billingCardCode: cardCompanyCode,
        cardCompanyId,
        cid: cardId,
        cardImageUrl,
        cardName,
        cardProductCode,
        cardType,
        maskedCardNumber,
        apiPauseSchedule,
        registerDatetime: registeredDateTime,
        rewardRate,
        benefitCardYn,
    }: PaymentMethodManagementCard): CardEasyPay => {
        // 여기를 분기처리해서 ed1, ed2, null로 나누기. 그래도 안된다.
        const getIsHyundaiPlccCard = () => {
            return cardCompanyId === CardCompanyId.HYUNDAI && cardProductCode === HYUNDAI_CARD_PRODUCT_CODE
        }
        const getHyundaiPlccCardType = () => {
            return cardProductCode === HYUNDAI_CARD_PRODUCT_CODE
                ? 'ed1'
                : cardProductCode === HYUNDAI_CARD_PRODUCT_CODE_VER2
                  ? 'ed2'
                  : null
        }

        return {
            cardCategory,
            cardCompanyCode,
            cardCompanyId,
            cardId,
            cardImageUrl,
            cardName,
            cardProductCode,
            cardType: mapCardTypeToCardType[cardType] ?? Card.CREDIT,
            maskedCardNumber,
            payServiceAvailable: true,
            payServiceMaintenanceSchedule: apiPauseSchedule
                ? {
                      beginDateTime: apiPauseSchedule.beginTime,
                      endDateTime: apiPauseSchedule.endTime,
                  }
                : null,
            registeredDateTime,
            isRewardCard: benefitCardYn === 'y',
            rewardAccumulateRate: rewardRate ?? 0,
            isHyundaiPlccCard: getIsHyundaiPlccCard(),
            hyundaiPlccCardType: getHyundaiPlccCardType(),
            /** 기본 더미 값 */
            cardOwnerType: null,
            longTermInstallment: false,
            rewardPointResidualAmount: 0,
            cardIssuerCode: null,
            invalidReason: null,
        }
    }
